import { HiOutlineSparkles, FiCheck, FiX } from '@/components/react-icons/main';
import { getDocContent } from '@/redux/actions/gravityActions';
import { sendAiChatMessage } from '@/redux/actions/uiActions/aiChatActions.js';
import selectors from '@/redux/selectors/selectors';
import * as Popover from '@radix-ui/react-popover';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { closeDocDrawer } from '../redux/actions/uiActions/drawerActions';
import Button from './Button.jsx';
import Input from './Input.jsx';
import Skeleton from './Skeleton';
import SliderHeader from './arguments/argumentOptionsSlider/SliderHeader.jsx';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';

const DocDrawer = () => {
  const [content, setContent] = useState('');
  const [processedHtml, setProcessedHtml] = useState('');
  const [popoverAnchorPoint, setPopoverAnchorPoint] = useState({ x: 0, y: 0 });
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedContent, setSelectedContent] = useState('');
  const { open, docId, highlightedElementId } = useSelector((state) => state.ui.docDrawer);
  const isLoading = useArgSelector(selectors.isLoading, 'getDocContent');

  useEffect(() => {
    if (open && docId) {
      getDocContent({ docId }, (data) => {
        if (data === undefined) {
          console.error(`DocDrawer was opened using an invalid docId: ${docId}`);
        }
        setContent(data);
      });
    }
  }, [docId, open]);

  // Process HTML content to add data-title attributes
  useEffect(() => {
    if (!content?.html) {
      return;
    }

    const processHtml = (htmlString) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');

      // Select all target elements
      const elements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6, p, ul, ol');
      elements.forEach((element) => {
        element.setAttribute('data-title', 'Ask AI about this');
      });

      return doc.body.innerHTML;
    };

    const processed = processHtml(content.html);
    setProcessedHtml(processed);
  }, [content?.html]);

  const handleClose = () => {
    closeDocDrawer();
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleAiSubmit = () => {
    sendAiChatMessage(inputValue, { context: selectedContent });
    setInputValue('');
    setIsPopoverOpen(false);
  };

  const handleAiInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAiSubmit();
    }
  };

  const handleContentClick = useCallback((e) => {
    // Don't trigger if text is being selected
    const selection = window.getSelection();
    if (selection.toString().length > 0) {
      return;
    }

    // Find the closest parent that matches our target elements
    const targetElement = e.target.closest('h1, h2, h3, h4, h5, h6, p, ul, ol');
    if (!targetElement) return;

    // Get the element's content and type
    // const elementType = targetElement.tagName.toLowerCase();
    const elementContent = targetElement.textContent;

    // Set the anchor point for the popover
    const rect = targetElement.getBoundingClientRect();
    setPopoverAnchorPoint({
      x: rect.left + window.scrollX,
      y: rect.top + rect.height + window.scrollY
    });

    setSelectedContent(elementContent);
    setIsPopoverOpen(true);
  }, []);

  useEffect(() => {
    if (open && highlightedElementId && !isLoading) {
      setTimeout(() => {
        const element = document.getElementById(`hash-${highlightedElementId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('highlight-flash');
          setTimeout(() => {
            element.classList.remove('highlight-flash');
          }, 1500);
        }
      }, 300);
    }
  }, [open, highlightedElementId, isLoading]);

  return (
    <>
      <div className={`comp-argument-option-slider ${open ? 'open' : ''}`}>
        <div className={'backing'} onClick={handleClose} />
        <div className={'content docs-slider'}>
          <SliderHeader title={content?.title} onClose={handleClose} />
          {!isLoading && <div className="markdown-content" onClick={handleContentClick} dangerouslySetInnerHTML={{ __html: processedHtml || content?.html }} />}
          {isLoading && (
            <div className="loading-content">
              <Skeleton count={25} skeletonStyle={{ height: 24, borderRadius: 4 }} />
            </div>
          )}
        </div>
      </div>
      <Popover.Root open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <Popover.Anchor style={{ position: 'absolute', left: popoverAnchorPoint.x, top: popoverAnchorPoint.y }} />
        <Popover.Portal>
          <Popover.Content className="doc-drawer-popover" side="bottom" align="start" sideOffset={4}>
            <div className="ai-input-wrapper">
              <Input icon={HiOutlineSparkles} value={inputValue} onChange={handleInputChange} placeholder="Ask AI about this" className={'borderless centered'} onKeyPress={handleAiInputKeyPress} />
              <Button size={'sm'} className={'ghost'} icon={FiX} onClick={() => setIsPopoverOpen(false)} />
              <Button size={'sm'} className={'ghost'} icon={FiCheck} onClick={handleAiSubmit} />
            </div>
            <Popover.Arrow className="doc-drawer-popover-arrow" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </>
  );
};

export default DocDrawer;
